/**
 * BESTIX v5.0 — Header Component
 * BESPOKIAN branded navigation with Sports dropdown
 */

import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, Phone, ChevronDown, Menu, X, User, LogOut, Ticket, Settings } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore, selectIsAuthenticated, selectUser } from '@/stores/auth.store';

// ============================================
// Types
// ============================================

interface NavItem {
  label: string;
  href: string;
  children?: { label: string; href: string }[];
}

// ============================================
// Navigation Links (using actual API category paths)
// ============================================

const navItems: NavItem[] = [
  { label: 'Events', href: '/events' },
  {
    label: 'Shows',
    href: '/events',
    children: [
      { label: 'Comedy', href: '/events?category=.1859.1986.1872.' },
      { label: 'Broadway', href: '/events?category=.1859.1989.1868.' },
      { label: 'Las Vegas', href: '/events?category=.1859.1987.1889.' },
    ],
  },
  {
    label: 'Sports',
    href: '/events',
    children: [
      { label: 'NFL', href: '/events?category=.1859.1988.1879.1959.' },
      { label: 'NBA', href: '/events?category=.1859.1988.1865.1971.' },
      { label: 'MLB', href: '/events?category=.1859.1988.1864.1969.' },
      { label: 'NHL', href: '/events?category=.1859.1988.1883.1972.' },
      { label: 'Soccer', href: '/events?category=.1859.1988.1913.2100.' },
      { label: 'Golf', href: '/events?category=.1859.1988.1880.' },
      { label: 'Tennis', href: '/events?category=.1859.1988.1916.' },
    ],
  },
  { label: 'Festivals', href: '/events?category=.1859.1987.1876.' },
  { label: 'Magazine', href: '/magazine' },
];

// ============================================
// Header Props
// ============================================

interface HeaderProps {
  variant?: 'light' | 'dark' | 'transparent';
}

// ============================================
// Header Component
// ============================================

export function Header({ variant = 'light' }: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Auth state
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const user = useAuthStore(selectUser);
  const logout = useAuthStore((state) => state.logout);

  // Get user initials for avatar
  const userInitials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : user?.email?.[0].toUpperCase() || 'U';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const isTransparent = variant === 'transparent' && !isScrolled;

  return (
    <>
      <header
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
          isTransparent ? 'bg-transparent' : 'bg-white shadow-sm'
        )}
      >
        <nav className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo - favicon.svg + BESPOKIAN TICKETS */}
            <Link
              to="/"
              className={cn(
                'flex items-center gap-3',
                isTransparent ? 'text-white' : 'text-[var(--color-navy)]'
              )}
            >
              <img
                src="/favicon.svg"
                alt=""
                className="h-12 w-auto shrink-0"
              />
              <div className="flex flex-col">
                <span className="font-playfair text-xl font-semibold tracking-wide">
                  BESPOKIAN
                </span>
                <span className={cn(
                  'text-[10px] tracking-[0.15em] uppercase',
                  isTransparent ? 'text-white/70' : 'text-gray-500'
                )}>
                  TICKETS
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8" ref={dropdownRef}>
              {navItems.map((item) => (
                <div key={item.label} className="relative">
                  {item.children ? (
                    <button
                      onClick={() => setOpenDropdown(openDropdown === item.label ? null : item.label)}
                      onMouseEnter={() => setOpenDropdown(item.label)}
                      className={cn(
                        'flex items-center gap-1 py-2 text-sm font-medium transition-colors',
                        isTransparent 
                          ? 'text-white hover:text-[var(--color-gold)]' 
                          : 'text-[var(--color-navy)] hover:text-[var(--color-gold)]'
                      )}
                    >
                      {item.label}
                      <ChevronDown className={cn(
                        'w-4 h-4 transition-transform',
                        openDropdown === item.label && 'rotate-180'
                      )} />
                    </button>
                  ) : (
                    <Link
                      to={item.href}
                      className={cn(
                        'py-2 text-sm font-medium transition-colors',
                        isTransparent 
                          ? 'text-white hover:text-[var(--color-gold)]' 
                          : 'text-[var(--color-navy)] hover:text-[var(--color-gold)]'
                      )}
                    >
                      {item.label}
                    </Link>
                  )}

                  {/* Dropdown Menu */}
                  {item.children && openDropdown === item.label && (
                    <div
                      className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl py-2 animate-fade-in z-50"
                      onMouseLeave={() => setOpenDropdown(null)}
                    >
                      {item.children.map((child) => (
                        <Link
                          key={child.label}
                          to={child.href}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[var(--color-gold)] transition-colors"
                          onClick={() => setOpenDropdown(null)}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  // Navigate to home page
                  if (location.pathname !== '/') {
                    navigate('/');
                  }
                  // Wait for navigation, then scroll to search and focus
                  setTimeout(() => {
                    const searchForm = document.querySelector('form[class*="max-w-2xl"]');
                    if (searchForm) {
                      searchForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      // Focus the search input
                      const searchInput = searchForm.querySelector('input[type="text"]') as HTMLInputElement;
                      if (searchInput) {
                        setTimeout(() => searchInput.focus(), 500);
                      }
                    }
                  }, 100);
                }}
                className={cn(
                  'p-2 rounded-full transition-colors',
                  isTransparent ? 'text-white hover:bg-white/10' : 'text-[var(--color-navy)] hover:bg-gray-100'
                )}
                aria-label="Search"
              >
                <Search className="w-5 h-5" />
              </button>

              <Link
                to="/help"
                className={cn(
                  'hidden md:flex items-center gap-2 p-2 rounded-full transition-colors',
                  isTransparent ? 'text-white hover:bg-white/10' : 'text-[var(--color-navy)] hover:bg-gray-100'
                )}
              >
                <Phone className="w-5 h-5" />
                <span className="text-sm font-medium">Concierge</span>
              </Link>

              {/* Auth UI */}
              {isAuthenticated ? (
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className={cn(
                      'flex items-center gap-2 p-1 rounded-full transition-colors',
                      isTransparent ? 'hover:bg-white/10' : 'hover:bg-gray-100'
                    )}
                  >
                    <div className={cn(
                      'w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold',
                      isTransparent 
                        ? 'bg-white/20 text-white' 
                        : 'bg-[var(--color-gold)] text-white'
                    )}>
                      {userInitials}
                    </div>
                    <ChevronDown className={cn(
                      'w-4 h-4 hidden sm:block transition-transform',
                      isTransparent ? 'text-white' : 'text-[var(--color-navy)]',
                      isUserMenuOpen && 'rotate-180'
                    )} />
                  </button>

                  {/* User Dropdown Menu */}
                  {isUserMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-xl py-2 animate-fade-in z-50">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="font-medium text-[var(--color-navy)] truncate">
                          {user?.name || 'Welcome'}
                        </p>
                        <p className="text-sm text-gray-500 truncate">{user?.email}</p>
                      </div>
                      <div className="py-1">
                        <Link
                          to="/account"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-[var(--color-gold)] transition-colors"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <User className="w-4 h-4" />
                          My Account
                        </Link>
                        <Link
                          to="/account?tab=orders"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-[var(--color-gold)] transition-colors"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <Ticket className="w-4 h-4" />
                          My Orders
                        </Link>
                        <Link
                          to="/account?tab=settings"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-[var(--color-gold)] transition-colors"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <Settings className="w-4 h-4" />
                          Settings
                        </Link>
                      </div>
                      <div className="border-t border-gray-100 py-1">
                        <button
                          onClick={() => {
                            logout();
                            setIsUserMenuOpen(false);
                            navigate('/');
                          }}
                          className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <LogOut className="w-4 h-4" />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/signin"
                  className={cn(
                    'hidden sm:flex items-center px-5 py-2.5 rounded-full text-sm font-medium transition-all',
                    isTransparent 
                      ? 'bg-white text-[var(--color-navy)] hover:bg-gray-100' 
                      : 'bg-[var(--color-navy)] text-white hover:bg-[var(--color-navy-light)]'
                  )}
                >
                  Sign In
                </Link>
              )}

              {/* Mobile Menu Button */}
              <button
                className={cn(
                  'lg:hidden p-2 rounded-md',
                  isTransparent ? 'text-white' : 'text-[var(--color-navy)]'
                )}
                onClick={() => setIsMobileMenuOpen(true)}
                aria-label="Open menu"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setIsMobileMenuOpen(false)} />
          <div className="fixed right-0 top-0 bottom-0 w-80 bg-white shadow-xl animate-slide-in-right">
            <div className="flex items-center justify-between p-4 border-b">
              <span className="font-playfair text-xl font-semibold text-[var(--color-navy)]">Menu</span>
              <button onClick={() => setIsMobileMenuOpen(false)} className="p-2">
                <X className="w-6 h-6 text-gray-500" />
              </button>
            </div>
            <nav className="p-4">
              {navItems.map((item) => (
                <div key={item.label} className="border-b border-gray-100 last:border-0">
                  <Link
                    to={item.href}
                    className="block py-3 text-[var(--color-navy)] font-medium hover:text-[var(--color-gold)]"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                  {item.children && (
                    <div className="pl-4 pb-2">
                      {item.children.map((child) => (
                        <Link
                          key={child.label}
                          to={child.href}
                          className="block py-2 text-sm text-gray-600 hover:text-[var(--color-gold)]"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t space-y-3">
              {isAuthenticated ? (
                <>
                  <div className="flex items-center gap-3 px-2 py-2 mb-2">
                    <div className="w-10 h-10 rounded-full bg-[var(--color-gold)] flex items-center justify-center text-white font-semibold">
                      {userInitials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[var(--color-navy)] truncate">{user?.name || 'Welcome'}</p>
                      <p className="text-sm text-gray-500 truncate">{user?.email}</p>
                    </div>
                  </div>
                  <Link
                    to="/account"
                    className="block w-full py-3 text-center bg-[var(--color-navy)] text-white rounded-full font-medium"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    My Account
                  </Link>
                  <button
                    onClick={() => {
                      logout();
                      setIsMobileMenuOpen(false);
                      navigate('/');
                    }}
                    className="block w-full py-3 text-center border border-red-200 text-red-600 rounded-full font-medium hover:bg-red-50 transition-colors"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/signin"
                    className="block w-full py-3 text-center bg-[var(--color-navy)] text-white rounded-full font-medium"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/events"
                    className="block w-full py-3 text-center border border-[var(--color-navy)] text-[var(--color-navy)] rounded-full font-medium"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Find Tickets
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header Spacer - only for non-transparent */}
      {variant !== 'transparent' && <div className="h-20" />}
    </>
  );
}

export default Header;
